import { useEffect, useState, useCallback, useRef, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Box, TextField, Grid, Typography, Chip, InputAdornment, Button, CircularProgress, Stack, Autocomplete } from '@mui/material';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { LocationOn, MyLocation, Search } from '@mui/icons-material';

// Fix for Leaflet default icon issues in React
const DefaultIcon = L.icon({
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

/**
 * Automatically moves the map view when coordinates change
 */
function ChangeView({ center }) {
    const map = useMap();
    useEffect(() => {
        if (center && center[0] && center[1]) {
            map.setView(center, 14);
        }
    }, [center, map]);
    return null;
}

ChangeView.propTypes = {
    center: PropTypes.arrayOf(PropTypes.number).isRequired
};

/**
 * Handles clicking on the map to set coordinates
 */
function MapEvents({ onLocationChange }) {
    useMapEvents({
        click(e) {
            const { lat, lng } = e.latlng;
            onLocationChange(lat, lng);
        }
    });
    return null;
}

MapEvents.propTypes = {
    onLocationChange: PropTypes.func.isRequired
};

export default function MapLocationPicker({ initialLat, initialLng, onChange, onAddressSelect, isLoading, label = 'Geographical Location' }) {
    const [lat, setLat] = useState(initialLat || 12.0022); // Default to Kano if null
    const [lng, setLng] = useState(initialLng || 8.5920);
    const [options, setOptions] = useState([]);
    const [searching, setSearching] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [isLoaded, setIsLoaded] = useState(!!window.google);
    const autocompleteService = useRef(null);
    const geocoder = useRef(null);

    useEffect(() => {
        if (initialLat) setLat(initialLat);
        if (initialLng) setLng(initialLng);
    }, [initialLat, initialLng]);

    // Load Google Maps Script
    useEffect(() => {
        console.log('Initializing Google Maps Script with Key:', GOOGLE_MAPS_KEY ? 'Present' : 'MISSING');
        
        if (window.google) {
            console.log('Google Maps already loaded');
            autocompleteService.current = new window.google.maps.places.AutocompleteService();
            geocoder.current = new window.google.maps.Geocoder();
            setIsLoaded(true);
            return;
        }

        if (GOOGLE_MAPS_KEY) {
            const scriptId = 'google-maps-script';
            if (document.getElementById(scriptId)) return;

            const script = document.createElement('script');
            script.id = scriptId;
            script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_KEY}&libraries=places`;
            script.async = true;
            script.defer = true;
            document.head.appendChild(script);
            
            script.onload = () => {
                console.log('Google Maps Script loaded successfully');
                autocompleteService.current = new window.google.maps.places.AutocompleteService();
                geocoder.current = new window.google.maps.Geocoder();
                setIsLoaded(true);
            };
            
            script.onerror = (e) => {
                console.error('Google Maps Script failed to load:', e);
            };
        } else {
            console.error('REACT_APP_GOOGLE_MAPS_KEY is missing in .env');
        }
    }, []);

    const extractAddressComponents = useCallback((results, newLat, newLng) => {
        if (!onAddressSelect || !results || results.length === 0) return;
        
        let state = '';
        let city = '';
        let street = '';
        let buildingNumber = '';
        let zipcode = '';
        let formattedAddress = results[0].formatted_address;

        for (const result of results) {
            if (!result.address_components) continue;
            
            result.address_components.forEach((c) => {
                if (!state && c.types.includes('administrative_area_level_1')) state = c.long_name;
                if (!city && (c.types.includes('administrative_area_level_2') || c.types.includes('locality'))) city = c.long_name;
                if (!street && c.types.includes('route')) street = c.long_name;
                if (!buildingNumber && c.types.includes('street_number')) buildingNumber = c.long_name;
                if (!zipcode && c.types.includes('postal_code')) zipcode = c.long_name;
            });

            if (state && city && street && buildingNumber && zipcode) break;
        }

        onAddressSelect({
            lat: newLat,
            lng: newLng,
            state: state,
            city: city,
            street: street,
            buildingNumber: buildingNumber,
            zipcode: zipcode,
            display_name: formattedAddress
        });
    }, [onAddressSelect]);

    const handleLocationChange = (newLat, newLng) => {
        const fixedLat = parseFloat(newLat).toFixed(6);
        const fixedLng = parseFloat(newLng).toFixed(6);
        setLat(fixedLat);
        setLng(fixedLng);
        if (onChange) onChange(fixedLat, fixedLng);

        // Reverse Geocode when map is clicked directly
        if (geocoder.current && onAddressSelect) {
            geocoder.current.geocode({ location: { lat: parseFloat(fixedLat), lng: parseFloat(fixedLng) } }, (results, status) => {
                if (status === 'OK' && results.length > 0) {
                    extractAddressComponents(results, parseFloat(fixedLat), parseFloat(fixedLng));
                }
            });
        }
    };

    const handleDirectInput = (type, value) => {
        if (type === 'lat') {
            setLat(value);
            if (onChange) onChange(value, lng);
        } else {
            setLng(value);
            if (onChange) onChange(lat, value);
        }
    };

    const fetchAddresses = useCallback((query) => {
        if (!query || query.length < 3 || !autocompleteService.current) return;
        setSearching(true);
        autocompleteService.current.getPlacePredictions(
            { input: query, componentRestrictions: { country: 'ng' } },
            (predictions, status) => {
                if (status === 'OK' && predictions) {
                    setOptions(predictions);
                } else {
                    if (status !== 'ZERO_RESULTS') {
                        console.error('Google Places Autocomplete Error:', status);
                    }
                    setOptions([]);
                }
                setSearching(false);
            }
        );
    }, []);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            fetchAddresses(inputValue);
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [inputValue, fetchAddresses]);

    const handleSelect = (event, value) => {
        if (value && geocoder.current) {
            geocoder.current.geocode({ placeId: value.place_id }, (results, status) => {
                if (status === 'OK' && results[0]) {
                    const result = results[0];
                    const newLat = result.geometry.location.lat();
                    const newLng = result.geometry.location.lng();
                    
                    setLat(newLat.toFixed(6));
                    setLng(newLng.toFixed(6));
                    if (onChange) onChange(newLat.toFixed(6), newLng.toFixed(6));

                    if (onAddressSelect) {
                        extractAddressComponents(results, newLat, newLng);
                    }
                }
            });
        }
    };

    const mapCenter = [parseFloat(lat) || 0, parseFloat(lng) || 0];

    return (
        <Box>
            <Typography variant="subtitle2" mb={2} color="text.secondary" display="flex" alignItems="center">
                <LocationOn sx={{ fontSize: 18, mr: 0.5 }} /> {label}
            </Typography>

            <Stack spacing={2}>
                <Autocomplete
                    fullWidth
                    options={options}
                    getOptionLabel={(option) => option.description || ''}
                    onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
                    onChange={handleSelect}
                    loading={searching}
                    filterOptions={(x) => x}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Search Address (Google Places)"
                            size="small"
                            placeholder="Enter street, city or business name"
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search sx={{ fontSize: 20, color: 'text.secondary' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <Fragment>
                                        {searching ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </Fragment>
                                )
                            }}
                        />
                    )}
                />

                <Box
                    sx={{
                        height: 300,
                        borderRadius: 2,
                        overflow: 'hidden',
                        position: 'relative',
                        border: '1px solid #ddd',
                        zIndex: 1
                    }}
                >
                    <MapContainer center={mapCenter} zoom={14} style={{ height: '100%', width: '100%' }}>
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                        <Marker position={mapCenter} icon={DefaultIcon} />
                        <ChangeView center={mapCenter} />
                        <MapEvents onLocationChange={handleLocationChange} />
                    </MapContainer>

                    <Box sx={{ position: 'absolute', bottom: 10, left: 10, zIndex: 1000 }}>
                        <Chip
                            label={`Lat: ${lat}, Lng: ${lng}`}
                            size="small"
                            sx={{ bgcolor: 'rgba(255,255,255,0.9)', fontWeight: 'bold', border: '1px solid #ccc' }}
                        />
                    </Box>
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <TextField
                            label="Latitude"
                            fullWidth
                            size="small"
                            value={lat}
                            onChange={(e) => handleDirectInput('lat', e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <MyLocation sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            label="Longitude"
                            fullWidth
                            size="small"
                            value={lng}
                            onChange={(e) => handleDirectInput('lng', e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <MyLocation sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                </Grid>
            </Stack>
        </Box>
    );
}

MapLocationPicker.propTypes = {
    initialLat: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    initialLng: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onChange: PropTypes.func,
    onAddressSelect: PropTypes.func,
    isLoading: PropTypes.bool,
    label: PropTypes.string
};
